import styled from "styled-components";
import logoSvg from "../../assets/logo.svg";

const FooterWrapper = styled.div`
  margin-top: 60px;

  @media (max-width: 768px) {
    margin-top: 40px;
  }
`;

const FooterContainer = styled.footer`
  background: #000;
  color: #fff;
  padding: 60px 20px 40px;
  position: relative;

  @media (max-width: 768px) {
    padding: 30px 15px 20px;
  }
`;

const FooterContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  align-items: start;
  justify-items: center;
  text-align: center;

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 20px;
    text-align: center;
    align-items: center;
  }

  @media (max-width: 1024px) and (min-width: 769px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
    align-items: center;
    text-align: center;
    width: 100%;

    &.address-section {
      display: none; // Hide address section on mobile
    }

    &.contacts-section {
      order: -1; // Show contacts first on mobile
    }

    &.phone-section {
      order: 0;
    }

    * {
      text-align: center !important;
    }
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #fff;
  text-transform: uppercase;
  letter-spacing: 1px;

  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 4px;
  }
`;

const ContactInfo = styled.div`
  p {
    margin: 8px 0;
    font-size: 14px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.9);
  }

  @media (max-width: 768px) {
    text-align: center;

    p {
      margin: 4px 0;
      font-size: 13px;
      line-height: 1.4;
    }

    &.phone-contacts {
      display: flex;
      justify-content: center;
      gap: 20px;
    }
  }
`;

const PhoneNumber = styled.a`
  color: rgba(255, 255, 255, 0.9);
  text-decoration: none;
  display: block;
  margin: 6px 0;
  font-size: 14px;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;
    text-decoration: underline;
  }

  @media (max-width: 768px) {
    margin: 3px 0;
    font-size: 13px;
  }
`;

const MobileOnlyName = styled.span`
  display: none;

  @media (max-width: 768px) {
    display: inline;
  }
`;

const DesktopOnlyPhone = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 12px;
  justify-content: center;

  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 8px;
    gap: 15px;
  }
`;

const SocialLink = styled.a`
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: color 0.2s ease;

  &:hover {
    color: #fff;

    svg {
      transform: translateY(-2px);
    }
  }

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.2s ease;
  }

  @media (max-width: 768px) {
    font-size: 13px;
    gap: 6px;

    svg {
      width: 18px;
      height: 18px;
    }
  }
`;

const Credits = styled.div`
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  position: relative;

  p:last-child {
    margin-top: 8px;
  }

  a {
    color: rgba(255, 255, 255, 0.8);
    text-decoration: none;
    transition: color 0.2s ease;

    &:hover {
      color: #fff;
      text-decoration: underline;
    }
  }

  @media (max-width: 768px) {
    margin-top: 20px;
    padding-top: 15px;
    font-size: 11px;

    p:last-child {
      margin-top: 4px;
    }
  }
`;

const FooterLogo = styled.img`
  position: absolute;
  bottom: 0;
  right: 0;
  width: 60px;
  height: auto;
  opacity: 0.3;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const FacebookIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
  </svg>
);

const MailIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-10 5L2 7" />
  </svg>
);

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <FooterWrapper>
      <FooterContainer>
        <FooterContent>
          <FooterSection className="address-section">
            <SectionTitle>Indirizzo</SectionTitle>
            <ContactInfo>
              <p>CO2 Snowboarding</p>
              <p>Via Berardo Maggi</p>
              <p>Gavardo - Brescia</p>
            </ContactInfo>
          </FooterSection>

          <FooterSection className="phone-section">
            <SectionTitle>Telefono</SectionTitle>
            <ContactInfo className="phone-contacts">
              <PhoneNumber href="tel:+393286530434">
                <MobileOnlyName>Tommy</MobileOnlyName>
                <DesktopOnlyPhone>Tommy +39 328 6530434</DesktopOnlyPhone>
              </PhoneNumber>
              <PhoneNumber href="tel:+393473168189">
                <MobileOnlyName>Fabio</MobileOnlyName>
                <DesktopOnlyPhone>Fabio +39 347 3168189</DesktopOnlyPhone>
              </PhoneNumber>
              <PhoneNumber href="tel:+393331219628">
                <MobileOnlyName>Willy</MobileOnlyName>
                <DesktopOnlyPhone>Willy +39 333 1219628</DesktopOnlyPhone>
              </PhoneNumber>
            </ContactInfo>
          </FooterSection>

          <FooterSection className="contacts-section">
            <SectionTitle>Contatti</SectionTitle>
            <ContactInfo>
              <p>info@co2snowboarding.it</p>
            </ContactInfo>
            <SocialLinks>
              <SocialLink
                href="https://www.facebook.com/CO2SNOWBOARDING/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FacebookIcon />
                Facebook
              </SocialLink>
              <SocialLink href="mailto:info@co2snowboarding.it">
                <MailIcon />
                Mail
              </SocialLink>
            </SocialLinks>
          </FooterSection>
        </FooterContent>

        <Credits>
          <p>© {currentYear} CO2 Snowboarding - Tutti i diritti riservati</p>
          <p>
            Webdesign{" "}
            <a
              href="http://www.tmspsndsgnr.it/"
              target="_blank"
              rel="noopener noreferrer"
            >
              TMSPSNDSGNR
            </a>{" "}
            • Development{" "}
            <a
              href="https://pablosproject.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              pablosproject
            </a>
          </p>
          <FooterLogo src={logoSvg} alt="CO2 Snowboarding" />
        </Credits>
      </FooterContainer>
    </FooterWrapper>
  );
};

